import React, { useState, useEffect, useRef } from "react";
import AsyncSelect from "react-select/async";
import api from "../utils/api";
import { toast } from "react-toastify";

// ── Inline mini category modal ─────────────────────────────────────────────
const InlineCategoryModal = ({ userId, onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [status, setStatus] = useState("Active");
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Category name is required"); return; }
    setSaving(true);
    try {
      const res = await api.post(`newcategories/${userId}`, { newTitle: name, newStatus: status, newDescription: "" });
      const createdId = res.data?.contact?._id || res.data?._id;
      toast.success("Category added!");
      onCreated({ value: createdId, label: name });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add category");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-bold text-gray-800">➕ New Category</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">✕</button>
        </div>
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="text-sm font-semibold text-gray-600 block mb-1">Category Name <span className="text-red-500">*</span></label>
            <input
              autoFocus
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Starters, Drinks, Desserts..."
              className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-600 block mb-1">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-5 py-2 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-semibold disabled:opacity-60 flex items-center gap-2">
              {saving && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
              Save Category
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Veg / Non-Veg dot indicator ────────────────────────────────────────────
const VegBadge = ({ type }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
    type === "Veg" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
  }`}>
    <span className={`w-2 h-2 rounded-full ${type === "Veg" ? "bg-green-500" : "bg-red-500"}`} />
    {type}
  </span>
);

// ── Unit options ───────────────────────────────────────────────────────────
const UNIT_OPTIONS = ["plates","pieces","grams","kilograms","milligrams","pounds","litres","milliliters","dozens","cups","glasses","bowls","bottles","cans","boxes","cartons"];

// ─────────────────────────────────────────────────────────────────────────────
const AddOneProduct = ({ userId, refreshProducts, onSuccess, existingData = null, onClose }) => {
  const [formData, setFormData] = useState({
    productName: "", type: "Veg", category: null,
    unit: "", stock: "", price: "", description: "",
    variants: [], addOns: [], image: null, isAvailable: true, allowOutOfStockOrders: false,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [showCatModal, setShowCatModal] = useState(false);
  // Key to force AsyncSelect to re-fetch after new category added
  const [selectKey, setSelectKey] = useState(0);

  // Initialize form from existing data
  useEffect(() => {
    if (existingData) {
      setFormData({
        productName: existingData.productName || "",
        type: (existingData.type && existingData.type.toLowerCase() !== "not_defined") ? existingData.type : "Veg",
        category: existingData.category
          ? { value: existingData.category._id, label: existingData.category.newTitle, categoryObj: existingData.category }
          : null,
        unit: existingData.unit || "",
        stock: existingData.stock || "",
        price: existingData.price || "",
        description: existingData.description || "",
        variants: existingData.variants || [],
        addOns: existingData.addOns || [],
        isAvailable: existingData.isAvailable !== undefined ? existingData.isAvailable : true,
        allowOutOfStockOrders: existingData.allowOutOfStockOrders || false,
        image: null,
      });
      setImagePreview(existingData.image || null);
    }
  }, [existingData]);

  const loadCategoryOptions = async (inputValue) => {
    try {
      const res = await api.get(`newcategories/${userId}?status=Active&search=${inputValue}&limit=20`);
      return res.data.data.map(cat => ({ value: cat._id, label: cat.newTitle, categoryObj: cat }));
    } catch { return []; }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(p => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const handleCategoryCreated = (newCat) => {
    // Auto-select the newly created category and force AsyncSelect refresh
    setFormData(p => ({ ...p, category: newCat }));
    setSelectKey(k => k + 1); // bust the cache
  };

  const validateForm = () => {
    if (!formData.productName.trim()) return "Product name is required!";
    if (!formData.category) return "Please select a category!";
    if (!formData.price) return "Product price is required!";
    if (isNaN(formData.price) || parseFloat(formData.price) < 0) return "Price must be a valid positive number!";

    for (let i = 0; i < formData.variants.length; i++) {
      const v = formData.variants[i];
      if (!v.name || !v.name.trim()) return `Variant ${i + 1} must have a name!`;
      if (!v.price || isNaN(v.price) || parseFloat(v.price) < 0) return `Variant ${i + 1} must have a valid price!`;
    }

    for (let i = 0; i < formData.addOns.length; i++) {
      const a = formData.addOns[i];
      if (!a.name || !a.name.trim()) return `Add-on ${i + 1} must have a name!`;
      if (!a.price || isNaN(a.price) || parseFloat(a.price) < 0) return `Add-on ${i + 1} must have a valid price!`;
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validateForm();
    if (err) { toast.error(err); return; }
    setIsSubmitting(true);
    try {
      const isUpdate = !!existingData;
      const data = new FormData();
      data.append("productName", formData.productName);
      data.append("type", formData.type);
      data.append("category", formData.category?.value || formData.category);
      data.append("unit", formData.unit);
      data.append("stock", Number(formData.stock) || 0);
      data.append("price", Number(formData.price) || 0);
      data.append("description", formData.description || "");
      data.append("isAvailable", formData.isAvailable);
      data.append("allowOutOfStockOrders", formData.allowOutOfStockOrders);
      data.append("variants", JSON.stringify(formData.variants || []));
      data.append("addOns", JSON.stringify(formData.addOns || []));
      if (formData.image) {
        data.append("image", formData.image);
      } else if (isUpdate && existingData && existingData.image && imagePreview === null) {
        // Explicitly clear the image on backend if it was removed
        data.append("image", "");
      }

      if (isUpdate && existingData._id) {
        await api.put(`product/${existingData._id}`, data);
      } else {
        await api.post(`product/${userId}`, data);
      }
      if (onSuccess) onSuccess(isUpdate ? "update" : "add");
      if (refreshProducts) refreshProducts();
      if (onClose) onClose();
    } catch (error) {
      let msg = "Error saving product!";
      if (error.response?.data?.message === "Product ID already exists") msg = "Product already exists!";
      else if (error.response?.data?.message) msg = error.response.data.message;
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Variant helpers ──────────────────────────────────────────────────────
  const addVariant = () => setFormData(p => ({ ...p, variants: [...p.variants, { name: "", price: "" }] }));
  const updateVariant = (idx, field, val) => {
    const arr = [...formData.variants]; arr[idx][field] = val;
    setFormData(p => ({ ...p, variants: arr }));
  };
  const removeVariant = (idx) => setFormData(p => ({ ...p, variants: p.variants.filter((_, i) => i !== idx) }));

  // ── Add-on helpers ───────────────────────────────────────────────────────
  const addAddon = () => setFormData(p => ({ ...p, addOns: [...p.addOns, { name: "", price: "" }] }));
  const updateAddon = (idx, field, val) => {
    const arr = [...formData.addOns]; arr[idx][field] = val;
    setFormData(p => ({ ...p, addOns: arr }));
  };
  const removeAddon = (idx) => setFormData(p => ({ ...p, addOns: p.addOns.filter((_, i) => i !== idx) }));

  const TABS = [
    { id: "general",  label: "General",          icon: "📋" },
    { id: "variants", label: `Variants (${formData.variants.length})`, icon: "🍕" },
    { id: "addons",   label: `Add-ons (${formData.addOns.length})`, icon: "🧀" },
  ];

  return (
    <>
      {/* ── Overlay ────────────────────────────────────────────────────── */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col"
          style={{ maxHeight: "92vh" }}
          onClick={e => e.stopPropagation()}
        >
          {/* ── Header ─────────────────────────────────────────────────── */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 rounded-t-2xl flex justify-between items-center shrink-0">
            <div>
              <h2 className="text-xl font-bold text-white">
                {existingData ? "✏️ Update Product" : "✨ Add New Product"}
              </h2>
              <p className="text-teal-200 text-xs mt-0.5">
                {existingData ? "Edit product details below" : "Fill in the details to add a new menu item"}
              </p>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white text-2xl leading-none">✕</button>
          </div>

          {/* ── Tabs ───────────────────────────────────────────────────── */}
          <div className="flex border-b border-gray-200 bg-gray-50 shrink-0 px-2">
            {TABS.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-semibold border-b-2 transition-all duration-200 ${
                  activeTab === tab.id
                    ? "border-teal-600 text-teal-700 bg-white"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* ── Body ───────────────────────────────────────────────────── */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-5">

              {/* ── GENERAL TAB ─────────────────────────────────────── */}
              {activeTab === "general" && (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                    {/* Product Name */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Product Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="productName"
                        type="text"
                        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 transition"
                        value={formData.productName}
                        onChange={handleChange}
                        required
                        placeholder="e.g. Paneer Tikka"
                        disabled={isSubmitting}
                        autoFocus
                      />
                    </div>

                    {/* Category */}
                    <div>
                      <div className="flex justify-between items-center mb-1.5">
                        <label className="text-sm font-semibold text-gray-700">
                          Category <span className="text-red-500">*</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowCatModal(true)}
                          disabled={isSubmitting}
                          className="flex items-center gap-1 text-xs font-semibold text-teal-600 hover:text-teal-800 bg-teal-50 hover:bg-teal-100 px-2.5 py-1 rounded-lg transition"
                        >
                          <span className="text-base leading-none">+</span> New Category
                        </button>
                      </div>
                      <AsyncSelect
                        key={selectKey}
                        cacheOptions={false}
                        loadOptions={loadCategoryOptions}
                        defaultOptions
                        placeholder="Search or select category..."
                        isSearchable
                        value={formData.category}
                        onChange={sel => {
                          setFormData(p => {
                            const newFormData = { ...p, category: sel };
                            // Auto-populate variants and addons if this is a new product and they are empty
                            if (sel && sel.categoryObj && !existingData) {
                              if (sel.categoryObj.variants && p.variants.length === 0) {
                                newFormData.variants = sel.categoryObj.variants.map(v => ({ name: v.name, price: v.price, isAvailable: true }));
                              }
                              if (sel.categoryObj.addOns && p.addOns.length === 0) {
                                newFormData.addOns = sel.categoryObj.addOns.map(a => ({ name: a.name, price: a.price, isAvailable: true }));
                              }
                            }
                            return newFormData;
                          });
                        }}
                        isDisabled={isSubmitting}
                        isClearable
                        maxMenuHeight={160}
                        menuPlacement="auto"
                        noOptionsMessage={() => "No categories found"}
                        classNamePrefix="rselect"
                        styles={{
                          control: (b, s) => ({
                            ...b, borderRadius: "0.75rem", borderColor: s.isFocused ? "#0d9488" : "#d1d5db",
                            boxShadow: s.isFocused ? "0 0 0 2px #99f6e4" : "none", minHeight: "42px", fontSize: "14px"
                          }),
                          option: (b, s) => ({ ...b, fontSize: "14px", backgroundColor: s.isSelected ? "#0d9488" : s.isFocused ? "#f0fdf4" : "white" }),
                        }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Price */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Base Price (₹) <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-semibold text-sm">₹</span>
                        <input
                          name="price"
                          type="number" step="0.01" min="0"
                          className="w-full border border-gray-300 rounded-xl pl-7 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 transition"
                          value={formData.price}
                          onChange={handleChange}
                          required placeholder="0.00"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    {/* Type */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Food Type</label>
                      <div className="flex gap-4 mt-2">
                        {["Veg", "Non-Veg"].map(t => (
                          <label key={t} className={`flex items-center gap-2 cursor-pointer px-4 py-2 rounded-xl border-2 transition-all ${
                            formData.type === t
                              ? t === "Veg" ? "border-green-500 bg-green-50" : "border-red-500 bg-red-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}>
                            <input type="radio" name="type" value={t} checked={formData.type === t}
                              onChange={handleChange} disabled={isSubmitting} className="hidden" />
                            <span className={`w-3 h-3 rounded-full ${t === "Veg" ? "bg-green-500" : "bg-red-500"}`} />
                            <span className={`text-sm font-semibold ${t === "Veg" ? "text-green-700" : "text-red-700"}`}>{t}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Unit */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Unit</label>
                      <select name="unit"
                        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 transition"
                        value={formData.unit} onChange={handleChange} disabled={isSubmitting}>
                        <option value="">Select Unit</option>
                        {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>

                    {/* Stock */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Stock Quantity</label>
                      <input name="stock" type="number" min="0"
                        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 transition"
                        value={formData.stock} onChange={handleChange} placeholder="e.g. 100" disabled={isSubmitting} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Description */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description</label>
                      <textarea name="description" rows={3}
                        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 transition"
                        value={formData.description} onChange={handleChange}
                        placeholder="Describe this item (optional)" disabled={isSubmitting} />
                    </div>

                    <div>
                      <div className="flex items-center space-x-3 mt-4">
                        <label className="flex items-center cursor-pointer">
                          <div className="relative">
                            <input type="checkbox" className="sr-only" checked={formData.allowOutOfStockOrders} disabled={isSubmitting}
                              onChange={(e) => setFormData(p => ({ ...p, allowOutOfStockOrders: e.target.checked }))} />
                            <div className={`block w-14 h-8 rounded-full transition-colors ${formData.allowOutOfStockOrders ? 'bg-orange-500' : 'bg-gray-300'}`}></div>
                            <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${formData.allowOutOfStockOrders ? 'transform translate-x-6' : ''}`}></div>
                          </div>
                        </label>
                        <div className="text-sm font-medium text-gray-700">
                          {formData.allowOutOfStockOrders ? "Allow out-of-stock orders" : "Strict stock tracking (Orders blocked if out-of-stock)"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Product Image</label>
                    <div className="flex items-center gap-4">
                      <label className={`flex flex-col items-center justify-center w-32 h-32 border-2 border-dashed rounded-xl cursor-pointer transition relative overflow-hidden ${
                        (imagePreview || formData.category?.categoryObj?.image) ? "border-teal-400" : "border-gray-300 hover:border-teal-400 bg-gray-50 hover:bg-teal-50"
                      }`}>
                        {imagePreview ? (
                          <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-xl" />
                        ) : formData.category?.categoryObj?.image ? (
                          <>
                            <img src={formData.category.categoryObj.image} alt="Category Default" className="w-full h-full object-cover rounded-xl opacity-80" />
                            <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center">
                               <span className="text-white text-[10px] font-bold px-2 py-1 bg-black/60 rounded">Category Default</span>
                            </div>
                          </>
                        ) : (
                          <div className="text-center p-3">
                            <div className="text-3xl text-gray-400">📷</div>
                            <p className="text-xs text-gray-400 mt-1">Click to upload</p>
                          </div>
                        )}
                        <input type="file" accept="image/*" className="hidden" disabled={isSubmitting}
                          onChange={e => {
                            const file = e.target.files[0];
                            if (file) { setFormData(p => ({ ...p, image: file })); setImagePreview(URL.createObjectURL(file)); }
                          }} />
                      </label>
                      <div className="flex flex-col gap-2">
                        {imagePreview && (
                          <button type="button" onClick={() => { setFormData(p => ({ ...p, image: null })); setImagePreview(null); }}
                            className="text-xs text-red-500 hover:text-red-700 text-left font-medium">Remove Custom Image</button>
                        )}
                        {!imagePreview && formData.category?.categoryObj?.image && (
                          <p className="text-xs text-gray-500 max-w-[150px]">Upload an image to override this category default.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── VARIANTS TAB ─────────────────────────────────────── */}
              {activeTab === "variants" && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex justify-between items-start gap-3">
                    <div>
                      <p className="text-sm font-semibold text-blue-800 mb-0.5">🍕 Variants</p>
                      <p className="text-xs text-blue-600">Options like Half/Full, Small/Medium/Large. A variant price overrides the base price.</p>
                    </div>
                    <button type="button" onClick={addVariant}
                      className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4 py-2 rounded-xl shadow-sm transition">
                      + Add Variant
                    </button>
                  </div>

                  {formData.variants.length === 0 ? (
                    <div className="flex flex-col items-center py-12 text-gray-400">
                      <span className="text-5xl mb-3">🍕</span>
                      <p className="text-sm">No variants yet. Click "+ Add Variant" to add one.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {formData.variants.map((v, idx) => (
                        <div key={idx} className="flex gap-3 items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                          <div className="flex-1">
                            <label className="text-xs text-gray-500 font-medium block mb-1">Name</label>
                            <input type="text" list="predefined-variants" placeholder="e.g. Medium"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                              value={v.name} onChange={e => updateVariant(idx, "name", e.target.value)} />
                            <datalist id="predefined-variants">
                              <option value="Half" />
                              <option value="Full" />
                              <option value="Quarter" />
                              <option value="Small" />
                              <option value="Medium" />
                              <option value="Large" />
                              <option value="Regular" />
                              <option value="Jumbo" />
                              <option value="Single" />
                              <option value="Double" />
                            </datalist>
                          </div>
                          <div className="w-28">
                            <label className="text-xs text-gray-500 font-medium block mb-1">Price (₹)</label>
                            <input type="number" placeholder="0.00" min="0" step="0.01"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                              value={v.price} onChange={e => updateVariant(idx, "price", e.target.value)} />
                          </div>
                          <div className="w-20 flex flex-col items-center justify-center">
                            <label className="text-xs text-gray-500 font-medium block mb-2">Status</label>
                            <label className="inline-flex items-center cursor-pointer">
                              <div className="relative">
                                <input type="checkbox" className="sr-only" checked={v.isAvailable !== false} onChange={(e) => updateVariant(idx, "isAvailable", e.target.checked)} />
                                <div className={`block w-10 h-6 rounded-full transition-colors ${v.isAvailable !== false ? 'bg-teal-500' : 'bg-gray-300'}`}></div>
                                <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${v.isAvailable !== false ? 'transform translate-x-4' : ''}`}></div>
                              </div>
                            </label>
                          </div>
                          <button type="button" onClick={() => removeVariant(idx)}
                            className="mt-5 text-red-400 hover:text-red-600 text-xl leading-none">✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── ADD-ONS TAB ──────────────────────────────────────── */}
              {activeTab === "addons" && (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex justify-between items-start gap-3">
                    <div>
                      <p className="text-sm font-semibold text-amber-800 mb-0.5">🧀 Add-ons</p>
                      <p className="text-xs text-amber-700">Optional extras like Extra Cheese or Toppings that add to the base item cost.</p>
                    </div>
                    <button type="button" onClick={addAddon}
                      className="shrink-0 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-4 py-2 rounded-xl shadow-sm transition">
                      + Add Add-on
                    </button>
                  </div>

                  {formData.addOns.length === 0 ? (
                    <div className="flex flex-col items-center py-12 text-gray-400">
                      <span className="text-5xl mb-3">🧀</span>
                      <p className="text-sm">No add-ons yet. Click "+ Add Add-on" to add one.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {formData.addOns.map((a, idx) => (
                        <div key={idx} className="flex gap-3 items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                          <div className="flex-1">
                            <label className="text-xs text-gray-500 font-medium block mb-1">Name</label>
                            <input type="text" placeholder="e.g. Extra Cheese"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                              value={a.name} onChange={e => updateAddon(idx, "name", e.target.value)} />
                          </div>
                          <div className="w-28">
                            <label className="text-xs text-gray-500 font-medium block mb-1">Price (₹)</label>
                            <input type="number" placeholder="0.00" min="0" step="0.01"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                              value={a.price} onChange={e => updateAddon(idx, "price", e.target.value)} />
                          </div>
                          <div className="w-20 flex flex-col items-center justify-center">
                            <label className="text-xs text-gray-500 font-medium block mb-2">Status</label>
                            <label className="inline-flex items-center cursor-pointer">
                              <div className="relative">
                                <input type="checkbox" className="sr-only" checked={a.isAvailable !== false} onChange={(e) => updateAddon(idx, "isAvailable", e.target.checked)} />
                                <div className={`block w-10 h-6 rounded-full transition-colors ${a.isAvailable !== false ? 'bg-teal-500' : 'bg-gray-300'}`}></div>
                                <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${a.isAvailable !== false ? 'transform translate-x-4' : ''}`}></div>
                              </div>
                            </label>
                          </div>
                          <button type="button" onClick={() => removeAddon(idx)}
                            className="mt-5 text-red-400 hover:text-red-600 text-xl leading-none">✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Footer ─────────────────────────────────────────────────── */}
            <div className="shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex justify-between items-center">
              <div className="flex gap-2">
                {formData.variants.length > 0 && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full font-medium">
                    {formData.variants.length} variant{formData.variants.length > 1 ? "s" : ""}
                  </span>
                )}
                {formData.addOns.length > 0 && (
                  <span className="text-xs bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full font-medium">
                    {formData.addOns.length} add-on{formData.addOns.length > 1 ? "s" : ""}
                  </span>
                )}
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={onClose} disabled={isSubmitting}
                  className="px-5 py-2.5 border border-gray-300 rounded-xl text-gray-700 text-sm font-semibold hover:bg-gray-100 transition disabled:opacity-50">
                  Cancel
                </button>
                <button type="submit" disabled={isSubmitting}
                  className="px-6 py-2.5 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white rounded-xl text-sm font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center gap-2">
                  {isSubmitting ? (
                    <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/><span>Saving...</span></>
                  ) : (
                    existingData ? "✓ Update Product" : "✨ Add Product"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* ── Inline Category Modal (on top, z-60) ──────────────────────── */}
      {showCatModal && (
        <InlineCategoryModal
          userId={userId}
          onClose={() => setShowCatModal(false)}
          onCreated={handleCategoryCreated}
        />
      )}
    </>
  );
};

export default AddOneProduct;
